import React from 'react'
import FormField from '@/components/common/FormField'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { errorTypes } from '@/utility/validation'
import { useFormContext } from 'react-hook-form'
import type { OnboardTabProps } from './onboard.types'

const OnboardBasicDetail: React.FC<OnboardTabProps> = ({ isLoading, isProfile, empId, handleNextStep, uploadFile }) => {
    const methods = useFormContext()
    const { register, formState: { errors }, setValue, watch } = methods

    const handleFileChange = async (e: any) => {
        const file = (e.target as HTMLInputElement).files?.[0]
        if (file && uploadFile) {
            const path = await uploadFile(file)
            if (path) {
                setValue('profilePhoto', path)
            }
        }
    }

    return (
        <div>
            <div className='mb-4'>
                <FormField
                    onChange={handleFileChange}
                    type="file"
                    name="profilePhoto"
                    label="Upload Image"
                />
                {watch('profilePhoto') && <p className="text-sm text-green-600 mt-1">File uploaded successfully</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                    {...register("employeeCode", { ...errorTypes.required('Employee Code') })}
                    name="employeeCode"
                    label="Employee Code"
                    type='text'
                    placeholder='Enter employee code'
                    error={errors.employeeCode?.message as string}
                />
                <FormField
                    {...register("name", { ...errorTypes.required('Name') })}
                    name="name"
                    label="Name"
                    type='text'
                    placeholder='Enter name'
                    error={errors.name?.message as string}
                />
                {/* <FormField
                    {...register("lastName", { ...errorTypes.required('Last Name') })}
                    name="lastName"
                    label="Last name"
                    type='text'
                    placeholder='Enter last name'
                    error={errors.lastName?.message as string}
                /> */}
                <FormField
                    {...register("gender", { ...errorTypes.required('Gender') })}
                    name="gender"
                    label="Gender"
                    type='select'
                    value={watch('gender')}
                    placeholder='Select gender'
                    options={[
                        { value: 'select gender', label: 'Select gender' },
                        { value: '0', label: 'Male' },
                        { value: '1', label: 'Female' },
                        { value: '2', label: 'Other' },
                    ]}
                    error={errors.gender?.message as string}
                />
                <FormField
                    {...register("email", { ...errorTypes.required('Email') })}
                    name="email"
                    label="Email"
                    type='email'
                    placeholder='Enter email'
                    disabled={isProfile}
                    error={errors.email?.message as string}
                />
                <FormField
                    {...register("phone", { ...errorTypes.required('Phone') })}
                    name="phone"
                    label="Mobile No."
                    type='text'
                    placeholder='Enter mobile No.'
                    disabled={isProfile}
                    minLength={10}
                    pattern="[0-9]{10}"
                    maxLength={10}
                    error={errors.phone?.message as string}
                />
                <FormField
                    {...register("dob", { ...errorTypes.required('Date') })}
                    name="dob"
                    label="Date of Birth"
                    type='date'
                    placeholder='Enter Date of Birth'
                    disabled={isProfile}
                    maxLength={10}
                    error={errors.dob?.message as string}
                />
                <FormField
                    {...register("fatherName", { ...errorTypes.required('Father Name') })}
                    name="fatherName"
                    label="Father name"
                    type='text'
                    placeholder='Enter father name'
                    disabled={isProfile}
                    maxLength={30}
                    error={errors.fatherName?.message as string}
                />
                <FormField
                    {...register("motherName", { ...errorTypes.required('Mother Name') })}
                    name="motherName"
                    label="Mother name"
                    type='text'
                    placeholder='Enter mother name'
                    disabled={isProfile}
                    maxLength={20}
                    error={errors.motherName?.message as string}
                />
                <FormField
                    {...register("ecpName", { ...errorTypes.required('Emergency Person') })}
                    name="ecpName"
                    label="Emergency Person"
                    type='text'
                    placeholder='Enter emergency person name'
                    disabled={isProfile}
                    maxLength={160}
                    error={errors.ecpName?.message as string}
                />
                <FormField
                    {...register("ecpPhone", { ...errorTypes.required('Emergency Contact'), ...errorTypes.phone })}
                    name="ecpPhone"
                    label="Emergency Contact"
                    type='text'
                    placeholder='Enter emergency contact'
                    disabled={isProfile}
                    minLength={10}
                    pattern="[0-9]{10}"
                    maxLength={10}
                    error={errors.ecpPhone?.message as string}
                />
                <FormField
                    {...register("joiningDate", { ...errorTypes.required('Joining Date') })}
                    name="joiningDate"
                    label="Joining Date"
                    type='date'
                    placeholder='Enter joining date'
                    disabled={isProfile}
                    maxLength={10}
                    error={errors.joiningDate?.message as string}
                />
                {!isProfile && (
                    <>
                        <FormField
                            {...register("status", { ...errorTypes.required('Status') })}
                            name="status"
                            label="Status"
                            type='select'
                            value={watch("status")}
                            options={[
                                { value: 'select status', label: 'Select status' },
                                { value: 'current', label: 'Current' },
                                { value: 'exEmployee', label: 'Ex-Employee' },
                                { value: 'transfer', label: 'Transfer' },
                            ]}
                            // disabled={!empId}
                            error={errors.status?.message as string}
                        />
                    </>
                )}
                <FormField
                    {...register("isMarried", { ...errorTypes.required('Marital Status') })}
                    name="isMarried"
                    label="Marital Status"
                    type='select'
                    value={watch("isMarried")}
                    options={[
                        { value: 'select marital status', label: 'Select marital status' },
                        { value: 'true', label: 'Married' },
                        { value: 'false', label: 'Unmarried' },
                    ]}
                    error={errors.isMarried?.message as string}
                />

            </div>
            <div className="flex items-center justify-end mt-6">
                <Button
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleNextStep('tab2', ['profilePhoto', 'employeeCode', 'name', 'gender', 'email', 'phone', 'dob', 'fatherName', 'motherName', 'ecpName', 'ecpPhone', 'joiningDate', 'status', 'isMarried'])}
                >
                    {isLoading ? <Spinner /> : (empId ? 'Update and Continue' : 'Save and Continue')}
                </Button>
            </div>
        </div>
    )
}

export default OnboardBasicDetail
